import { useTranslation } from 'react-i18next'
import useFormStatus from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useFormStatus'
import { useDispatch, useSelector } from 'react-redux'
import { lazy } from 'react'
import {
  selectCurrentAddressData,
  selectPersonalContactSubscriptionData,
  selectPreferencesContactSubscriptionData,
  selectProfessionalContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { updateClientData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { subscriptionFormActionsV2 } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormV2Sagas'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { useEffect } from 'react'
import { selectAuthCommonData } from '../../../Login/AuthSelectors'
import {
  selectAdditionalContactSubscriptionData,
  selectContactConfig,
  selectContactFormErrors,
} from '../../SubscriptionForm/SubscriptionFormSelectors'
import { addClientError, removeClientError } from '../../SubscriptionForm/SubscriptionFormSlice'
import { modulesConfig } from '../../config'
import useDoorToDoorPartnerChecklist from '../DoorToDoorPartnerChecklist/useDoorToDoorPartnerChecklist'
import { selectIsDoorToDoorPartner } from '@/store/PageConfiguration/PageConfigurationSelectors'

const CompanyIdComponents = {
  it: lazy(() => import('./Fields/IT/CompanyId/CompanyId')),
  default: lazy(() => import('./Fields/Default/CompanyId')),
}

const useContact = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const setErrors = (key, message) => dispatch(addClientError({ key, message }))
  const removeErrors = key => dispatch(removeClientError(key))
  const { lang, country } = useSelector(selectAuthData)
  const {
    firstName,
    firstName2,
    lastName,
    lastName2,
    civility,
    civility2,
    phone2,
    phone1,
    email,
    secondName,
    zohoId,
    birthdate,
    bornCity,
  } = useSelector(selectPersonalContactSubscriptionData)

  const { emailIsLoading, prefilledEmail, siretIsLoading, siretIsValid } = useSelector(selectContactConfig)

  const {
    dataCollectionAuth,
    isNewsletterRegistered,
    clientType,
    neverBounceResult,
    isEkomiRegistered,
    createEkomiInteraction,
    whatsappConsent,
  } = useSelector(selectPreferencesContactSubscriptionData)
  const { referral, referralFirstName, referralLastName } = useSelector(selectAdditionalContactSubscriptionData)
  const { civilityList, clientTypeOptions, activitiesList } = useSelector(selectAuthCommonData)
  const { socialReason, siret, apeCode, activity } = useSelector(selectProfessionalContactSubscriptionData)
  const { formIsLoaded, zohoIsInValid, isEditable } = useFormStatus()
  const { doorToDoorPartnerChecklistCompleted } = useDoorToDoorPartnerChecklist()
  const isDoorToDoorPartner = useSelector(selectIsDoorToDoorPartner)

  const errors = useSelector(selectContactFormErrors)
  const currentAddressData = useSelector(selectCurrentAddressData)

  const calculateClientTypeValidationWarning = () => {
    const electricityMeterData = currentAddressData.electricityMeter || {}
    const hasElectricityMeterData = Boolean(electricityMeterData.pdlEligibilityData)

    if (!clientType || !hasElectricityMeterData) {
      return false
    }

    const meterIsProfessional = electricityMeterData.pdlEligibilityData?.elec?.isProfessional
    if (meterIsProfessional !== null && meterIsProfessional !== undefined) {
      const mapping = {
        true: 'pro',
        false: 'part',
      }

      const meterClientType = mapping[meterIsProfessional]
      return clientType !== meterClientType
    }

    return false
  }

  const hasClientTypeWarning = calculateClientTypeValidationWarning()

  const customerUpdate = payload => {
    dispatch(updateClientData(payload))
  }

  const addContact = () => {
    dispatch({
      type: subscriptionFormActionsV2.CONTACT_ADD_CONTACT_V2,
    })
  }

  const verifyEmail = email => {
    dispatch({
      type: subscriptionFormActionsV2.CONTACT_MODULE_VERIFY_EMAIL_V2,
      email,
      emailErrorMsg: t('subscriptionForm:customer_module.email.error'),
    })
  }

  const captionForceEmail = boolean => {
    customerUpdate({
      data: {
        prefilledEmail: {
          status: boolean,
          message: boolean ? t('subscriptionForm:customer_module.email.forcedCaption') : '',
        },
      },
      name: 'config',
    })
  }

  const verifySiret = () => {
    dispatch({
      type: subscriptionFormActionsV2.CONTACT_MODULE_VERIFY_SIRET_V2,
      siret,
      siretErrorMsg: t('subscriptionForm:customer_module.proSiret.error'),
    })
  }

  const authorizeDataCollectionHandler = value => {
    if (value === true) {
      customerUpdate({ data: { dataCollectionAuth: value }, name: 'preferences' })
      return
    }
    customerUpdate({
      data: {
        name: '',
        surname: '',
        phone1: '',
        civility: '',
        birthdate: '',
      },
      name: 'personal',
    })
    customerUpdate({ data: { dataCollectionAuth: value }, name: 'preferences' })
  }

  const LocalizedCompanyIdComponent = CompanyIdComponents[country] ?? CompanyIdComponents.default
  const config = modulesConfig[country].customer ?? {}
  const finalOptionsCivility = []
  civilityList.forEach(option => {
    finalOptionsCivility.push({
      [option]: t('subscriptionForm:customer_module.civility.' + option),
    })
  })

  useEffect(() => {
    if (location.pathname.endsWith('/add')) {
      addContact()
    }
  }, [location.pathname])

  return {
    config,
    finalOptionsCivility,
    formIsLoaded,
    zohoIsInValid,
    customerUpdate,
    isEditable,
    t,
    verifyEmail,
    setErrors,
    removeErrors,
    captionForceEmail,
    authorizeDataCollectionHandler,
    verifySiret,
    errors,
    emailIsLoading,
    dataCollectionAuth,
    firstName,
    lastName,
    lastName2,
    firstName2,
    secondName,
    civility,
    civility2,
    phone1,
    phone2,
    isNewsletterRegistered,
    isEkomiRegistered,
    createEkomiInteraction,
    whatsappConsent,
    email,
    prefilledEmail,
    lang,
    country,
    socialReason,
    siret,
    activity,
    apeCode,
    activitiesList,
    clientTypeOptions,
    clientType,
    zohoId,
    birthdate,
    bornCity,
    neverBounceResult,
    siretIsLoading,
    siretIsValid,
    referral,
    referralFirstName,
    referralLastName,
    hasClientTypeWarning,
    doorToDoorPartnerChecklistCompleted,
    isDoorToDoorPartner,
    LocalizedCompanyIdComponent,
  }
}

export default useContact
